import { Block, Color, Icon, Text } from '@snowflake/core-ui';
import React, { useRef, useState } from 'react';
import './StartupOfferPage.scss';
import { FormattedMessage } from 'react-intl';
import { CardTemplate } from '../../components/Cards/shared/CardTemplate';
import { SignupCardButton } from '../../components/Cards/forms/SignupCardV2/SignupCardButton';
import { CortexLogo } from '../../components/CortexLogo';
import { Footer } from '../../components/Footer';
import { InternalHeaders } from '../../utils/InternalHeaders';
import * as configs from '../../utils/configurations';
import { StartupOfferTerms } from './StartupOfferTerms';
import {
  buildStartupApplicationFields,
  recordTrialSignupConversion,
  submitStartupApplication,
} from './submitStartupApplication';

const ELIGIBILITY = [
  'Startup founded within the last 10 years',
  'Pre-Series B (most recent funding round must be within the last 12 months, if applicable)',
  "GitHub account at least 2 years old from the date of application and maintained in good standing throughout (active and not suspended, restricted, or in violation of GitHub's Terms of Service or Acceptable Use Policies)",
  'One subscription per person',
  'Must be 18 years or older',
  'Must reside and operate in countries where Snowflake trial accounts are available',
];

const PageTitle = () => {
  return (
    <Block className="trial-page-title" display="flex" flexDirection="column" alignItems="center">
      <Text style={{ alignSelf: 'stretch', textAlign: 'center', margin: 0 }}>
        <Text as="span" className="trial-page-title-text" style={{ color: '#29B5E8' }}>
          BUILD FASTER WITH{' '}
        </Text>
        <Text as="span" className="trial-page-title-text" style={{ color: '#000' }}>
          SNOWFLAKE
        </Text>
      </Text>
      <Text className="trial-page-description">
        One platform for your data, apps and AI workflows
      </Text>
    </Block>
  );
};

const Eligibility = () => {
  return (
    <Block
      className="trial-left-container"
      display="flex"
      flexDirection="column"
      justifyContent="flex-start"
    >
      <Text weight="bold" fontSize={22} marginBottom="8px">
        <FormattedMessage id="12 months of Cortex Code, free" />
      </Text>
      <Text marginBottom="16px" style={{ color: '#3a4452' }}>
        <FormattedMessage id="Apply for 12 months of Snowflake Cortex Code (CoCo) completely free. An exclusive offer for startups." />
      </Text>
      {ELIGIBILITY.map(item => (
        <Block key={item} display="flex" marginBottom="12px">
          <Block marginTop="6px" marginRight="12px">
            <img src="/static/images/rectangle-bullet.svg" alt="" />
          </Block>
          <Text size="large" style={{ color: '#1a1a1a' }}>
            {item}
          </Text>
        </Block>
      ))}
      <Text weight="bold" marginTop="16px" style={{ color: '#1570a6' }}>
        <FormattedMessage id="Once approved, you'll receive an email with your personal sign-up link." />
      </Text>
    </Block>
  );
};

function isDryRunRequest(): boolean {
  return new URLSearchParams(window.location.search).get('dryRun') === '1';
}

const Confirmation = (props: { email: string; dryRun: boolean }) => {
  const lead = props.dryRun
    ? 'Dry run. Nothing was sent.'
    : props.email
      ? `We have your application for ${props.email}. This does not create a Snowflake account.`
      : 'We have your application. This does not create a Snowflake account.';
  return (
    <Block display="flex" justifyContent="center" paddingTop="48px" paddingBottom="48px">
      <Block maxWidth="480px" width="100%">
        <CardTemplate>
          <Block display="flex" flexDirection="column" alignItems="center">
            <Block borderRadius="50%" overflow="hidden" marginBottom="16px">
              <Icon
                icon="checkmark"
                size={32}
                color={Color.White}
                backgroundColor={Color.Green70}
              />
            </Block>
            <Text fontSize={22} weight="bold">
              <FormattedMessage id="Application received" />
            </Text>
            <Text marginTop="8px" textAlign="center" style={{ color: '#3a4452' }}>
              {lead}
            </Text>
            <ol className="startup-offer-steps">
              <li>
                <span className="startup-offer-step-num">1</span>
                <FormattedMessage id="We review your application against the eligibility rules." />
              </li>
              <li>
                <span className="startup-offer-step-num">2</span>
                <FormattedMessage id="If you are approved, we email a one-time sign-up link to the address you entered. Check spam if it does not arrive." />
              </li>
              <li>
                <span className="startup-offer-step-num">3</span>
                <FormattedMessage id="That link is what creates the account. No payment method is required to enroll." />
              </li>
            </ol>
            <Text marginTop="8px" size="small" textAlign="center" style={{ color: '#6b7585' }}>
              <FormattedMessage id="Approval is not guaranteed. The offer is limited to the first 500 approved participants. Meeting the eligibility rules is a minimum requirement." />
            </Text>
          </Block>
        </CardTemplate>
      </Block>
    </Block>
  );
};

export const StartupOfferPage = () => {
  const recaptchaRef = useRef(null);
  const [submitted, setSubmitted] = useState(() => window.location.hash === '#submitted');
  const [email, setEmail] = useState('');
  const [agree, setAgree] = useState(false);
  const [showAgreeError, setShowAgreeError] = useState(false);

  const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!agree) {
      setShowAgreeError(true);
      return;
    }
    const form = event.currentTarget;
    const input = {
      fullName: (form.elements.namedItem('fullName') as HTMLInputElement).value.trim(),
      email: (form.elements.namedItem('email') as HTMLInputElement).value.trim(),
      website: (form.elements.namedItem('website') as HTMLInputElement).value.trim(),
      plan: (form.elements.namedItem('plan') as HTMLTextAreaElement).value.trim(),
    };
    const fields = buildStartupApplicationFields(input);
    if (!isDryRunRequest()) {
      const layer = configs.getDataLayer();
      if (layer) {
        recordTrialSignupConversion(layer as Array<{ event: string }>);
      }
      submitStartupApplication(fields, document);
    }
    setEmail(input.email);
    setSubmitted(true);
    window.history.replaceState(
      null,
      '',
      `${window.location.pathname}${window.location.search}#submitted`,
    );
    window.scrollTo(0, 0);
  };

  return (
    <Block className="trial-page-container unified-style startup-offer-page">
      <InternalHeaders />
      <CortexLogo />
      <Block className="startup-offer-body">
        <PageTitle />
        {!submitted && (
          <Block display="flex" justifyContent="center" paddingTop={32}>
            <Block className="trial-toggle-container">
              <Block className="trial-toggle-button active single">
                <Text as="span" className="trial-toggle-label-primary">
                  <FormattedMessage id="Free CoCo" />
                </Text>
                <Text as="span" className="trial-toggle-label-secondary">
                  <FormattedMessage id="for startups" />
                </Text>
              </Block>
            </Block>
          </Block>
        )}
        {submitted ? (
          <Confirmation email={email} dryRun={isDryRunRequest()} />
        ) : (
          <Block className="trial-responsive-flex-container" position="relative">
            <Eligibility />
            <Block
              className="trial-right-container"
              display="flex"
              flexDirection="column"
              justifyContent="center"
              maxWidth="520px"
            >
              <CardTemplate title="Apply for the startup offer">
                <form id="startup-offer-form" onSubmit={onSubmit}>
                  <div className="startup-offer-field">
                    <label htmlFor="startup-full-name">
                      <FormattedMessage id="Full name" />
                    </label>
                    <input
                      id="startup-full-name"
                      name="fullName"
                      type="text"
                      autoComplete="name"
                      required
                    />
                  </div>
                  <div className="startup-offer-field">
                    <label htmlFor="startup-email">
                      <FormattedMessage id="Email address" />
                    </label>
                    <input
                      id="startup-email"
                      name="email"
                      type="email"
                      autoComplete="email"
                      required
                    />
                  </div>
                  <div className="startup-offer-field">
                    <label htmlFor="startup-website">
                      <FormattedMessage id="Company website" />
                    </label>
                    <input
                      id="startup-website"
                      name="website"
                      type="text"
                      inputMode="url"
                      placeholder="https://"
                      pattern={'\\s*\\S+\\s*'}
                      title="Enter a website without spaces"
                      required
                    />
                  </div>
                  <div className="startup-offer-field">
                    <label htmlFor="startup-plan">
                      <FormattedMessage id="How do you plan to use CoCo?" />
                    </label>
                    <textarea id="startup-plan" name="plan" required />
                  </div>
                  <Text weight="semi-bold" marginTop="18px" size="small">
                    <FormattedMessage id="Program details and terms of service" />
                  </Text>
                  <StartupOfferTerms />
                  <label className="startup-offer-check">
                    <input
                      type="checkbox"
                      checked={agree}
                      onChange={change => {
                        setAgree(change.target.checked);
                        if (change.target.checked) setShowAgreeError(false);
                      }}
                    />
                    <span>
                      <FormattedMessage id="Yes, I acknowledge and agree" />
                    </span>
                  </label>
                  {showAgreeError && (
                    <Text color={Color.Red50} size="small" marginTop="8px">
                      <FormattedMessage id="Check the box to agree before applying." />
                    </Text>
                  )}
                  <Block marginTop="16px">
                    <SignupCardButton
                      width="100%"
                      onClick={click => {
                        click.preventDefault();
                        const form = document.getElementById(
                          'startup-offer-form',
                        ) as HTMLFormElement;
                        form.requestSubmit();
                      }}
                    >
                      <FormattedMessage id="Apply" />
                    </SignupCardButton>
                  </Block>
                </form>
              </CardTemplate>
            </Block>
          </Block>
        )}
      </Block>
      <Block className="startup-offer-footer">
        <Footer recaptchaRef={recaptchaRef} />
      </Block>
    </Block>
  );
};
